import { useEffect, useState } from "react";
import * as S from "./style";
import {
  activePubOwnerApi,
  deletePubOwnerApi,
  getPubOwnersApi,
  PubOwnerDomain,
  PubOwnerResponse,
  PubOwnerRole,
} from "../../api/cafe";
import DropBox from "../../component/DropBox";
import { domainOptions, getRoleName } from "../../utils/owner";
import { pubState } from "../../recoil/pub.atom";
import { useRecoilValue } from "recoil";
import OwnerEdit from "./components/edit";
import { UserState } from "../../recoil/user.atom";
import { useNavigate } from "react-router-dom";

const OwnerManagement = () => {
  const user = useRecoilValue(UserState);
  const pub = useRecoilValue(pubState);
  const [list, setList] = useState<PubOwnerResponse[]>([]);
  const [allList, setAllList] = useState<PubOwnerResponse[]>([]);
  const [isPopup, setIsPopup] = useState(false);
  const [owner, setOwner] = useState<PubOwnerResponse | null>(null);
  const [search, setSearch] = useState("");
  const [filterDomain, setFilterDomain] = useState<PubOwnerDomain | null>(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (!pub) {
      navigate("/");
      return;
    }
    if (pub.role === PubOwnerRole.OWNER || pub.domains.length === 0) return;
    navigate("/");
    alert("해당 페이지 접근 권한이 없습니다. ");
    return;
  }, [navigate, pub]);

  const getList = async () => {
    if (!pub) return;
    try {
      const res = await getPubOwnersApi(pub.id);
      setList(res);
      setAllList(res);
    } catch (error) {
      console.error(error);
    }
  };

  useEffect(() => {
    getList();
  }, [pub]);

  useEffect(() => {
    let filteredList = allList;
    if (search) {
      filteredList = allList.filter((item) => {
        return item.username.includes(search) || item.nickname.includes(search);
      });
    }
    if (filterDomain !== null) {
      filteredList = filteredList.filter((item) => {
        return item.domains.includes(filterDomain);
      });
    }
    setList(filteredList);
  }, [search, filterDomain, allList]);

  const header = [
    { label: "이름", flex: 1 },
    { label: "닉네임", flex: 1 },
    { label: "전화번호", flex: 1 },
    { label: "접근 가능 토너먼트", flex: 1.5 },
    { label: "보유 권한", flex: 1 },
    { label: "관리", flex: 1 },
    { label: "적용여부", flex: 1 },
    { label: "삭제", flex: 1 },
  ];

  const handleDelete = async (item: PubOwnerResponse) => {
    const confirm = window.confirm(
      `${item.username}의 권한을 완전히 삭제하시겠습니까?`,
    );
    if (!confirm) return;
    try {
      await deletePubOwnerApi(item.userId, item.pubId);
      alert("삭제되었습니다.");
      getList();
    } catch (e: any) {
      if (e.status === 403) {
        alert(e.response.data.message);
        return;
      }
      alert(e.message);
    }
  };

  const handleActive = async (item: PubOwnerResponse) => {
    const confirm = window.confirm(
      `${item.username}의 권한을 ${!item.isActive ? "적용" : "해제"}하시겠습니까?`,
    );
    if (!confirm) return;
    try {
      await activePubOwnerApi(item.id, !item.isActive);
      alert(`${!item.isActive ? "적용" : "해제"}되었습니다.`);
      getList();
    } catch (e: any) {
      if (e.status === 403) {
        alert(e.response.data.message);
        return;
      }
      alert(e.message);
    }
  };

  const isMineItem = (item: PubOwnerResponse) => {
    return item.userId === user?.id;
  };

  return (
    <S.OwnerContainer>
      {isPopup && (
        <OwnerEdit
          onClose={() => {
            setIsPopup(false);
            setOwner(null);
          }}
          owner={owner}
          getData={getList}
        />
      )}
      <S.OwnerHeader>
        <div className="title">보안</div>
        <div className="button" onClick={() => setIsPopup(true)}>
          유저 추가
        </div>
      </S.OwnerHeader>
      <S.OwnerContentWrapper>
        <S.OwnerContentHeader>
          <div>접근 가능 유저 목록</div>
          <S.OwnerContentHeaderRight>
            <input
              type="text"
              placeholder="닉네임 또는 이름을 검색하세요. "
              onChange={(e) => setSearch(e.target.value)}
            />
            <DropBox
              label="보유 권한"
              list={domainOptions}
              value={filterDomain}
              setValue={setFilterDomain}
            />
          </S.OwnerContentHeaderRight>
        </S.OwnerContentHeader>
        <S.OwnerContentTable>
          <S.OwnerContentTableHeader>
            {header.map((item) => (
              <div key={item.label} style={{ flex: item.flex }}>
                {item.label}
              </div>
            ))}
          </S.OwnerContentTableHeader>
          {list.map((item) => (
            <S.OwnerContentTableItem key={item.id}>
              <div>{item.username}</div>
              <div>{item.nickname}</div>
              <div>{item.phoneNumber}</div>
              <div className="tournamentIds">
                {item.role === PubOwnerRole.STAFF
                  ? item.tournamentIds.join(", ")
                  : "ALL"}
              </div>
              <div>{getRoleName(item.domains, item.role)}</div>
              {isMineItem(item) ? (
                <>
                  <div />
                  <div />
                  <div />
                </>
              ) : (
                <>
                  <div>
                    <div
                      className="button"
                      onClick={() => {
                        setOwner(item);
                        setIsPopup(true);
                      }}
                    >
                      설정
                    </div>
                  </div>
                  <div>
                    <S.OwnerToggleItem
                      isOn={item.isActive}
                      onClick={() => handleActive(item)}
                    >
                      <S.OwnerToggleCircle isOn={item.isActive} />
                    </S.OwnerToggleItem>
                  </div>
                  <div>
                    <img
                      src="/image-web/icon/close.svg"
                      alt="delete"
                      onClick={() => handleDelete(item)}
                    />
                  </div>
                </>
              )}
            </S.OwnerContentTableItem>
          ))}
        </S.OwnerContentTable>
      </S.OwnerContentWrapper>
    </S.OwnerContainer>
  );
};

export default OwnerManagement;
